// public/api-config.json is copied to the deployment root by Vue CLI. Change apiMode in that
// deployed JSON file to switch endpoints without rebuilding; refresh open tabs.
// Exclude /api-config.json from CDN rules that ignore query strings or force cache.
const gatewayOrigin = 'https://gateway.gocloudeasy.com';
const configTimeoutMs = 2000;
let apiBaseUrl;
let loading;

export function resolveApiBaseUrl(config, directOrigin) {
  if (!config || !['direct', 'gateway'].includes(config.apiMode)) {
    throw new Error('apiMode must be direct or gateway.');
  }
  const origin = config.apiMode === 'gateway' ? gatewayOrigin : String(directOrigin || '').replace(/\/+$/, '');
  const parsed = new URL(origin);
  if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password
    || parsed.pathname !== '/' || parsed.search || parsed.hash) {
    throw new Error('API URL must be an HTTP(S) origin without a path.');
  }
  return origin;
}

export function getApiBaseUrl() {
  if (!apiBaseUrl) {
    throw new Error('API configuration has not loaded.');
  }
  return apiBaseUrl;
}

// Admin API calls always go to the IP-restricted admin host, whatever apiMode says.
// Builds without VUE_APP_ADMIN_API_URL (e.g. staging) use the normal API.
export function getAdminApiBaseUrl() {
  const adminOrigin = process.env.VUE_APP_ADMIN_API_URL;
  return adminOrigin ? resolveApiBaseUrl({ apiMode: 'direct' }, adminOrigin) : getApiBaseUrl();
}

export function loadApiConfig() {
  if (!loading) {
    loading = Promise.resolve().then(() => {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      let timeout;
      // Bound startup even without AbortController or when the response body stalls.
      const deadline = new Promise((resolve, reject) => {
        timeout = setTimeout(() => {
          reject(new Error('API configuration timed out.'));
          if (controller) controller.abort();
        }, configTimeoutMs);
      });
      // Root-relative even on /aud/, /sgd/, /usd/ and history-mode routes.
      const base = process.env.BASE_URL || '/';
      const url = `${base}api-config.json?t=${Date.now()}`;
      const request = Promise.resolve().then(() => window.fetch(url, {
        cache: 'no-store',
        credentials: 'same-origin',
        ...(controller ? { signal: controller.signal } : {}),
      })).then((response) => {
        if (!response.ok) {
          throw new Error('Could not load API configuration.');
        }
        return response.json();
      }).then((config) => resolveApiBaseUrl(config, process.env.VUE_APP_API_URL));
      return Promise.race([request, deadline]).finally(() => clearTimeout(timeout));
    }).catch(() => resolveApiBaseUrl({ apiMode: 'direct' }, process.env.VUE_APP_API_URL))
      .then((origin) => {
        apiBaseUrl = origin;
        return apiBaseUrl;
      });
  }
  // Config failures use the build-time direct API. Keep that choice for this
  // page's lifetime, even if config arrives late; never retry payments elsewhere.
  return loading;
}
